import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, Sparkles, Crown, Utensils, Calendar, MapPin, Star, Award } from "lucide-react";

import { api, absUrl } from "../../lib/api";
import { Section, Eyebrow, SectionTitle, FadeIn } from "../../components/public/Section";
import InquiryForm from "../../components/public/InquiryForm";

const HERO_IMG = "https://images.unsplash.com/photo-1587271407850-8d438ca9fdf2?auto=format&fit=crop&w=2000&q=80";
const ABOUT_IMG = "https://images.unsplash.com/photo-1681270543584-8e541a1bb056?auto=format&fit=crop&w=1400&q=80";

export default function Home() {
  const [data, setData] = useState({ site: null, packages: [], services: [], categories: [], testimonials: [], gallery: [] });

  useEffect(() => {
    Promise.all([
      api.get("/public/site"),
      api.get("/public/packages"),
      api.get("/public/services"),
      api.get("/public/menu-categories"),
      api.get("/public/testimonials"),
      api.get("/public/gallery"),
    ]).then(([s, p, sv, cat, t, g]) => {
      setData({ site: s.data, packages: p.data, services: sv.data, categories: cat.data, testimonials: t.data, gallery: g.data });
    }).catch(() => {});
  }, []);

  const { site, packages, services, categories, testimonials, gallery } = data;

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${site?.hero?.image_url || HERO_IMG})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease: "easeOut" }}
          className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8 pb-24 md:pb-32 pt-32"
        >
          <div className="gold-divider mb-6 max-w-xs">
            <span className="label-eyebrow whitespace-nowrap">Established Vadodara · Est. 2007</span>
          </div>
          <h1 className="serif text-ivory text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight max-w-4xl">
            Royal Indian <em className="text-shimmer not-italic">Hospitality</em>,<br />
            Plated With Precision.
          </h1>
          <p className="text-ivory/85 mt-7 max-w-2xl text-base md:text-lg leading-relaxed">
            {site?.hero?.sub || "Vadodara's most-loved luxury catering house — 18 years of weddings, corporate galas, destination events & industrial canteens. Curated by Akash Sharma."}
          </p>
          <div className="flex flex-wrap gap-3 mt-9">
            <Link to="/contact" className="btn-gold" data-testid="hero-inquire-btn">
              Inquire For Your Event <ArrowRight size={14} />
            </Link>
            <Link to="/packages" className="btn-outline" data-testid="hero-packages-btn">
              Explore Packages
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
            {[
              { n: site?.stats?.events || "2400+", l: "Events Crafted" },
              { n: site?.stats?.cuisines || "15+", l: "Cuisines" },
              { n: site?.stats?.years || "18", l: "Years of Hospitality" },
              { n: site?.stats?.cities || "12", l: "Cities Served" },
            ].map((s, i) => (
              <FadeIn key={i} delay={0.2 + i * 0.1}>
                <div className="border-l border-gold/40 pl-4">
                  <div className="serif text-3xl md:text-4xl text-gold">{s.n}</div>
                  <div className="text-ivory/70 text-[11px] tracking-royal uppercase mt-1">{s.l}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </motion.div>
      </section>

      {/* SERVICES MARQUEE */}
      <div className="bg-navy-900 border-y border-gold/15 py-5 overflow-hidden">
        <div className="marquee">
          <div className="marquee-track items-center text-gold/70 text-sm tracking-[0.3em] uppercase whitespace-nowrap">
            {[...Array(2)].map((_, idx) => (
              <div key={idx} className="flex gap-12 items-center">
                {["Wedding Catering", "Corporate Galas", "Industrial Canteens", "Destination Weddings", "Royal Buffet", "Live Counters", "VIP Concierge"].map((t, i) => (
                  <span key={i} className="flex items-center gap-12"><Sparkles size={12} className="text-gold" /> {t}</span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ABOUT preview */}
      <Section testId="about-preview-section">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <FadeIn>
            <div className="relative">
              <div className="relative overflow-hidden">
                <img src={ABOUT_IMG} className="w-full h-[520px] object-cover" alt="Chef plating" />
                <div className="absolute inset-0 border border-gold/30" />
              </div>
              <div className="absolute -bottom-6 -right-6 glass-navy px-6 py-4 text-ivory hidden md:block">
                <div className="serif text-4xl text-gold">18+</div>
                <div className="label-eyebrow text-ivory/70">Years of Royal Service</div>
              </div>
            </div>
          </FadeIn>
          <FadeIn delay={0.15}>
            <Eyebrow>The House of Balaji</Eyebrow>
            <h2 className="serif text-4xl md:text-5xl leading-tight">
              A culinary atelier where <em className="text-shimmer not-italic">tradition meets theatre.</em>
            </h2>
            <p className="text-navy-900/50 mt-5 leading-relaxed">
              Founded by Master Caterer <span className="text-gold font-semibold">{site?.founder_name || "Akash Sharma"}</span>, Shree Balaji Caterers has hosted Gujarat's most celebrated weddings, corporate gatherings and destination events. Our kitchens marry Gujarati soul with global craft — every plate plated as if for a royal table.
            </p>
            <ul className="mt-7 space-y-3">
              {["FSSAI Certified · ISO Hygiene Standards", "Hotel-grade waitstaff & uniformed crew", "Hot-line transport + on-site finishing kitchens", "Bespoke menu design with the Founder"].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-navy-900/50 text-sm">
                  <Crown size={16} className="text-gold mt-0.5" />{t}
                </li>
              ))}
            </ul>
            <Link to="/about" className="btn-gold mt-8" data-testid="about-cta">
              Read Our Story <ArrowRight size={14} />
            </Link>
          </FadeIn>
        </div>
      </Section>

      {/* PACKAGES */}
      <Section dark testId="packages-preview-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <Eyebrow dark>Signature Packages</Eyebrow>
            <h2 className="serif text-ivory text-4xl md:text-6xl leading-tight">
              Choose your <em className="text-shimmer not-italic">royal canvas.</em>
            </h2>
          </div>
          <p className="text-ivory/65 max-w-md">Five carefully composed packages — from intimate Silver to the legendary Royal Signature reserved for once-in-a-lifetime celebrations.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {packages.map((p, i) => (
            <FadeIn key={p.id} delay={i * 0.08}>
              <div
                className={`relative h-full p-7 border ${p.is_signature ? "border-gold bg-gradient-to-b from-gold/10 to-transparent" : "border-gold/15"} hover:border-gold/60 transition group`}
                data-testid={`package-card-${p.slug}`}
              >
                {p.is_signature && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy-900 text-[10px] tracking-[0.25em] uppercase px-3 py-1 font-bold">Signature</div>
                )}
                <div className="serif text-2xl text-ivory">{p.name}</div>
                <div className="label-eyebrow text-gold/80 mt-1">{p.tagline}</div>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="serif text-5xl text-gold">₹{p.price}</span>
                  <span className="text-ivory/50 text-xs">/plate</span>
                </div>
                <ul className="mt-6 space-y-2 text-ivory/75 text-[13px] leading-relaxed">
                  {(p.inclusions || []).slice(0, 5).map((inc, j) => (
                    <li key={j} className="flex gap-2"><Sparkles size={12} className="text-gold mt-1 shrink-0" />{inc}</li>
                  ))}
                </ul>
                <Link to="/packages" className="mt-7 inline-flex items-center gap-1 text-gold text-[11px] tracking-[0.25em] uppercase hover:gap-2 transition-all" data-testid={`package-cta-${p.slug}`}>
                  View Details <ArrowRight size={12} />
                </Link>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* CUISINES */}
      <Section testId="cuisines-section">
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow>Multi-Cuisine Atelier</Eyebrow>
          <h2 className="serif text-4xl md:text-6xl leading-tight">
            Fifteen worlds, <em className="text-shimmer not-italic">one table.</em>
          </h2>
          <p className="text-navy-900/60 mt-5">From Kathiyawadi Bajra Rotla to Wood-fired Margherita and Mango Sticky Rice — every cuisine prepared by specialty chefs.</p>
        </div>

        <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {categories.map((c, i) => (
            <FadeIn key={c.id} delay={i * 0.04}>
              <Link
                to={`/menus?cat=${c.slug}`}
                className="block group border border-navy-900/10 hover:border-gold transition p-5 royal-card bg-white relative overflow-hidden"
                data-testid={`cuisine-${c.slug}`}
              >
                <Utensils size={18} className="text-gold" />
                <div className="mt-3 serif text-xl">{c.name}</div>
                <div className="text-xs tracking-wider text-navy-900/50 mt-1">{c.tagline}</div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* SERVICES BENTO */}
      <Section dark testId="services-bento-section">
        <SectionTitle dark title="Where we" italic="serve royalty." subtitle="From floral mandaps to industrial canteens — same precision, same warmth." center />
        <div className="grid md:grid-cols-3 gap-4">
          {services.slice(0, 6).map((s, i) => (
            <FadeIn key={s.id} delay={i * 0.06}>
              <Link
                to="/services"
                className="block p-7 border border-gold/15 hover:border-gold/70 transition group relative overflow-hidden h-full"
                data-testid={`service-card-${s.slug}`}
              >
                <Award size={22} className="text-gold" />
                <h3 className="serif text-2xl text-ivory mt-5">{s.title}</h3>
                <p className="text-ivory/60 text-sm mt-2">{s.tagline}</p>
                <ArrowRight size={16} className="text-gold mt-5 group-hover:translate-x-2 transition-transform" />
                <div className="absolute -bottom-12 -right-12 w-32 h-32 rounded-full bg-gold/5 group-hover:bg-gold/15 transition-colors" />
              </Link>
            </FadeIn>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/services" className="btn-outline" data-testid="services-view-all">View All Services <ArrowRight size={14} /></Link>
        </div>
      </Section>

      {/* GALLERY */}
      <Section testId="gallery-preview-section">
        <SectionTitle title="From our" italic="recent celebrations" center subtitle="A glimpse of stations, plating and ambient design from past events." />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {gallery.slice(0, 8).map((g, i) => (
            <FadeIn key={g.id} delay={i * 0.06}>
              <div className={`relative overflow-hidden group ${i % 3 === 0 ? "row-span-2" : ""}`}>
                <img
                  src={absUrl(g.image_url)} alt={g.title}
                  className={`w-full ${i % 3 === 0 ? "h-[420px]" : "h-[200px]"} object-cover transition-transform duration-700 group-hover:scale-110`}
                  data-testid={`gallery-img-${i}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <span className="text-ivory serif text-lg">{g.title}</span>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/gallery" className="btn-gold" data-testid="gallery-view-all">View Full Gallery <ArrowRight size={14} /></Link>
        </div>
      </Section>

      {/* TESTIMONIALS */}
      <Section dark testId="testimonials-preview-section" className="bg-paisley">
        <SectionTitle dark title="Whispers from" italic="our patrons." center />
        <div className="grid md:grid-cols-3 gap-5">
          {testimonials.slice(0, 3).map((t, i) => (
            <FadeIn key={t.id} delay={i * 0.1}>
              <div className="p-7 border border-gold/20 h-full bg-navy-900/60">
                <div className="flex gap-1 text-gold mb-4">
                  {[...Array(t.rating || 5)].map((_, j) => <Star key={j} size={14} fill="currentColor" />)}
                </div>
                <p className="text-ivory/85 serif text-lg leading-relaxed italic">"{t.quote}"</p>
                <div className="mt-6 border-t border-gold/15 pt-4">
                  <div className="serif text-gold">{t.name}</div>
                  <div className="text-ivory/50 text-[11px] tracking-royal uppercase">{t.event}</div>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* INQUIRY CTA */}
      <Section testId="inquiry-section" className="bg-gradient-to-b from-ivory to-ivory-200">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <Eyebrow>Begin the Conversation</Eyebrow>
            <h2 className="serif text-4xl md:text-6xl leading-tight">
              Let's plate your <em className="text-shimmer not-italic">unforgettable evening.</em>
            </h2>
            <p className="text-navy-900/50 mt-5 max-w-md">
              Share a few details and our concierge will reach out within 24 hours with a curated proposal, sample menus and a private tasting invite.
            </p>
            <div className="mt-8 space-y-4 text-sm">
              <div className="flex items-center gap-3"><Phone size={16} className="text-gold" /> <a href={`tel:${site?.phone || "+919328797924"}`} className="font-semibold">{site?.phone || "+91 9328797924"}</a></div>
              <div className="flex items-center gap-3"><Calendar size={16} className="text-gold" /> Open 7 days · 9:00 to 21:00</div>
              <div className="flex items-center gap-3"><MapPin size={16} className="text-gold" /> {site?.address || "Vadodara, Gujarat"}</div>
            </div>
          </div>
          <InquiryForm packages={packages} />
        </div>
      </Section>
    </>
  );
}
